import React, { useState, useRef, useEffect, Fragment } from "react";
import * as XLSX from "xlsx";
import MessageBanner from "./MessageBanner";
import "./MessageBanner.scss";
import axios from "axios";
import { useSubheader } from "../../../_metronic/layout";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../_metronic/_partials/controls";

const BulkPayUpload = () => {
  const suhbeader = useSubheader();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uiMessage, setUiMessage] = useState(null);
  const [backendErrors, setBackendErrors] = useState(null);

  const expectedHeaders = [
    "Payment Date",
    "Month",
    "Start Date",
    "End Date",
    "Student ID",
    "Student Name",
    "Course Code",
    "Order Amount",
    "Taxes",
    "Payment Method",
    "Internal Notes",
    "Branch",
    "Customer Notes(if any)",
  ];

  useEffect(() => {
    suhbeader.setTitle("Bulk Payment Upload");
  }, [suhbeader]);

  // Helper function to display backend errors using MessageBanner
  const displayBackendError = (
    errorResponse,
    defaultMessage = "An error occurred"
  ) => {
    let errorMessage = defaultMessage;
    let errorType = "error";

    if (errorResponse) {
      // Use the message from backend if available
      if (errorResponse.message) {
        errorMessage = errorResponse.message;
      } else if (errorResponse.error) {
        errorMessage = errorResponse.error;
      }

      // Set appropriate error type based on the step
      switch (errorResponse.step) {
        case "field_validation":
          errorType = "error";
          break;
        case "db_lookup":
          errorType = "error";
          break;
        case "payment_validation":
          errorType = "error";
          break;
        case "upload":
          errorType = "error";
          break;
        default:
          errorType = "error";
      }
    }

    setUiMessage({
      type: errorType,
      text: errorMessage,
    });

    // Store detailed backend errors for rendering
    setBackendErrors(errorResponse);
  };

  function validateHeaders(sheetHeaders) {
    const normalizedExpected = expectedHeaders.map((h) =>
      h.toLowerCase().trim()
    );

    // Filter out empty headers from sheet
    const filteredSheetHeaders = sheetHeaders.filter(
      (h) => h && h.toString().trim() !== ""
    );

    const normalizedActual = filteredSheetHeaders.map((h) =>
      h.toLowerCase().trim()
    );

    if (normalizedActual.length < normalizedExpected.length) {
      return {
        valid: false,
        error: `Expected at least ${normalizedExpected.length} columns but found ${filteredSheetHeaders.length}.`,
      };
    }

    // Check if first 13 columns match expected headers
    for (let i = 0; i < normalizedExpected.length; i++) {
      if (normalizedExpected[i] !== normalizedActual[i]) {
        return {
          valid: false,
          error: `Header mismatch at column "${expectedHeaders[i]}": expected "${expectedHeaders[i]}", found "${filteredSheetHeaders[i]}".`,
        };
      }
    }

    return { valid: true };
  }

  function mapRowKeysToExpectedHeaders(row, headerMap) {
    const newRow = {};
    for (const [key, value] of Object.entries(row)) {
      const mappedKey = headerMap[key.toLowerCase().trim()];
      if (mappedKey) {
        newRow[mappedKey] = value;
      }
      // Removed console.warn for unmapped headers
    }
    return newRow;
  }

  function validateRow(row, index) {
    const errors = [];
    const rowNum = index + 2;

    const isValidDate = (date) => {
      return !isNaN(new Date(date).getTime());
    };

    if (!row["Payment Date"] || !isValidDate(row["Payment Date"])) {
      errors.push('Invalid or missing "Payment Date".');
    }

    if (!row["Month"] || row["Month"].toString().trim() === "") {
      errors.push('"Month" is required.');
    } else {
      const monthValue = row["Month"];
      const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];

      const monthIndex = monthNames.findIndex(
        (m) => m.toLowerCase() === monthValue.toLowerCase().trim()
      );

      if (monthIndex === -1) {
        errors.push(
          `Invalid "Month" value: ${row["Month"]}. It should be a full month name.`
        );
      }
    }

    if (!row["Start Date"] || !isValidDate(row["Start Date"])) {
      errors.push('Invalid or missing "Start Date".');
    }
    if (!row["End Date"] || !isValidDate(row["End Date"])) {
      errors.push('Invalid or missing "End Date".');
    }
    if (
      isValidDate(row["Start Date"]) &&
      isValidDate(row["End Date"]) &&
      new Date(row["Start Date"]) > new Date(row["End Date"])
    ) {
      errors.push('"Start Date" must be before or equal to "End Date".');
    }

    if (!row["Student ID"] || row["Student ID"].toString().trim() === "") {
      errors.push('"Student ID" is required.');
    }

    if (!row["Student Name"] || row["Student Name"].toString().trim() === "") {
      errors.push('"Student Name" is required.');
    }

    if (!row["Course Code"] || row["Course Code"].toString().trim() === "") {
      errors.push('"Course Code" is required.');
    }

    const orderAmount = parseFloat(row["Order Amount"]);
    if (isNaN(orderAmount) || orderAmount <= 0) {
      errors.push('"Order Amount" must be a positive number.');
    }

    const taxes = parseFloat(row["Taxes"]);
    if (taxes < 0) {
      errors.push('"Taxes" must be a number equal or greater than zero.');
    }

    const allowedMethods = [
      "Credit Card",
      "Debit Card",
      "Cash",
      "Electronic Fund Transfer",
      "Cheque",
    ];

    // Case-insensitive payment method validation
    const paymentMethod = row["Payment Method"]
      ? row["Payment Method"].toString().trim()
      : "";
    const foundMethod = allowedMethods.find(
      (method) => method.toLowerCase() === paymentMethod.toLowerCase()
    );

    if (!paymentMethod || !foundMethod) {
      errors.push(
        `"Payment Method" is required and must be one of: ${allowedMethods.join(
          ", "
        )}.`
      );
    } else {
      // Normalize the payment method to match the exact case
      row["Payment Method"] = foundMethod;
    }

    if (!row["Branch"] || row["Branch"].toString().trim() === "") {
      errors.push('"Branch" is required.');
    }

    const MAX_NOTES_LENGTH = 1000;
    if (
      row["Customer Notes(if any)"] &&
      row["Customer Notes(if any)"].length > MAX_NOTES_LENGTH
    ) {
      errors.push(
        `"Customer Notes" cannot exceed ${MAX_NOTES_LENGTH} characters.`
      );
    }

    if (
      row["Internal Notes"] &&
      row["Internal Notes"].length > MAX_NOTES_LENGTH
    ) {
      errors.push(
        `"Internal Notes" cannot exceed ${MAX_NOTES_LENGTH} characters.`
      );
    }

    return errors.length > 0
      ? { row: rowNum, studentId: row["Student ID"], errors }
      : null;
  }

  function normalizeDates(data) {
    return data.map((row) => ({
      ...row,
      "Payment Date": new Date(row["Payment Date"]).toISOString().slice(0, 10),
      "Start Date": new Date(row["Start Date"]).toISOString().slice(0, 10),
      "End Date": new Date(row["End Date"]).toISOString().slice(0, 10),
    }));
  }

  function findDuplicates(data) {
    const seen = new Map();
    const duplicates = [];

    data.forEach((row, index) => {
      const key = [
        row["Student ID"],
        row["Course Code"],
        row["Start Date"],
        row["End Date"],
      ].join("|");

      if (seen.has(key)) {
        duplicates.push({
          row: index + 2,
          duplicateOf: seen.get(key),
          studentId: row["Student ID"],
          errors: [
            "Duplicate record for Student ID, Course Code, and date range.",
          ],
        });
      } else {
        seen.set(key, index + 2);
      }
    });

    return duplicates;
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }

    if (!file) {
      setSelectedFile(null);
      setParsedData(null);
      setValidationResult(null);
      setUploadResult(null);
      setUiMessage(null);
      setBackendErrors(null);
      return;
    }

    setSelectedFile(new File([file], file.name, { type: file.type }));

    // Clear previous states
    setParsedData(null);
    setValidationResult(null);
    setUploadResult(null);
    setUiMessage(null);
    setBackendErrors(null);
  };

  async function handleValidateClick() {
    if (!selectedFile || !(selectedFile instanceof File)) {
      setUiMessage({
        type: "error",
        text: "Please select a file first.",
      });
      return;
    }

    try {
      setIsValidating(true);
      setUiMessage(null);
      setBackendErrors(null);

      const allowedTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];
      const fileExtension = selectedFile.name
        .split(".")
        .pop()
        .toLowerCase();

      if (
        !allowedTypes.includes(selectedFile.type) ||
        fileExtension !== "xlsx"
      ) {
        setUiMessage({
          type: "error",
          text: "Invalid file type. Please upload a valid Excel (.xlsx) file.",
        });
        setIsValidating(false);
        return;
      }

      if (selectedFile.size > 2 * 1024 * 1024) {
        setUiMessage({
          type: "error",
          text: "File is too large. Maximum allowed size is 2MB.",
        });
        setIsValidating(false);
        return;
      }

      const data = await selectedFile.arrayBuffer();
      const workbook = XLSX.read(data);
      if (workbook.SheetNames.length !== 1) {
        setUiMessage({
          type: "error",
          text: "Please upload an Excel file with exactly one sheet.",
        });
        setIsValidating(false);
        return;
      }

      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const rawJsonData = XLSX.utils.sheet_to_json(worksheet, {
        defval: "",
        raw: false,
        cellDates: true,
      });

      const jsonData = rawJsonData.map((row) => {
        const normalizedRow = {};
        Object.keys(row).forEach((key) => {
          const trimmedKey = key.trim();
          let value = row[key];
          if (typeof value === "string") {
            value = value.trim();
          }
          normalizedRow[trimmedKey] = value;
        });
        return normalizedRow;
      });

      if (jsonData.length === 0) {
        setUiMessage({
          type: "error",
          text: "The sheet is empty",
        });
        setIsValidating(false);
        return;
      }

      const sheetHeaders = Object.keys(jsonData[0]);
      const headerValidation = validateHeaders(sheetHeaders);

      // Filter out empty headers and create header map for only the first 13 expected headers
      const filteredHeaders = sheetHeaders.filter(
        (h) => h && h.toString().trim() !== ""
      );
      const headerMap = {};
      filteredHeaders
        .slice(0, expectedHeaders.length)
        .forEach((header, index) => {
          headerMap[header.toLowerCase().trim()] = expectedHeaders[index];
        });

      const correctedData = jsonData.map((row) =>
        mapRowKeysToExpectedHeaders(row, headerMap)
      );

      if (!headerValidation.valid) {
        setUiMessage({
          type: "error",
          text: `Header Validation Error: ${headerValidation.error}`,
        });
        setIsValidating(false);
        return;
      }

      const rowErrors = [];
      correctedData.forEach((row, index) => {
        try {
          const validation = validateRow(row, index);
          if (validation) rowErrors.push(validation);
        } catch (err) {
          rowErrors.push({
            row: index + 2,
            studentId: row["Student ID"] || "N/A",
            errors: [`Unexpected error during validation: ${err.message}`],
          });
        }
      });

      const normalizedData = normalizeDates(correctedData);
      const duplicateErrors = findDuplicates(correctedData);
      const combinedErrors = [...rowErrors, ...duplicateErrors];

      if (combinedErrors.length > 0) {
        setValidationResult({
          isValid: false,
          validationErrors: combinedErrors,
        });
        setParsedData(normalizedData);
        setIsValidating(false);
        return;
      }

      setParsedData(normalizedData);
      setValidationResult({ isValid: true, validationErrors: [] });
      setUiMessage({
        type: "success",
        text: `Validation successful! ${normalizedData.length} records are ready for upload.`,
      });
      setIsValidating(false);
    } catch (error) {
      setUiMessage({
        type: "error",
        text: `Unexpected error during file processing: ${error.message}`,
      });
      setIsValidating(false);
    }
  }

  const handleUploadClick = async () => {
    if (!validationResult || !validationResult.isValid) {
      setUiMessage({
        type: "error",
        text: "Please validate the file and fix all errors before uploading.",
      });
      return;
    }

    setIsUploading(true);
    setUploadResult(null);
    setUiMessage(null);
    setBackendErrors(null);

    try {
      // Define base URL from env or fallback
      const API_BASE = "";

      const { data: result } = await axios.post(
        `${API_BASE}/api/bulk-payments/bulk-upload`,
        parsedData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      setUploadResult(result);

      if (result.success) {
        setUiMessage({
          type: "success",
          text: `Upload successful! ${result.recordsProcessed ||
            result.data?.totalRecords ||
            0} records processed.`,
        });

        // Clear form on success
        setSelectedFile(null);
        setParsedData(null);
        setValidationResult(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = null;
        }
      } else {
        displayBackendError(
          result,
          "Upload failed. Please check the errors below."
        );
      }
    } catch (error) {
      console.error("Upload error:", error);

      if (error.response) {
        // Server responded with a non-2xx status
        displayBackendError(
          error.response.data,
          "Upload failed. Please check the errors below."
        );
      } else if (error.request) {
        // No response received
        setUiMessage({
          type: "error",
          text: "Network error occurred during upload. Please try again.",
        });
      } else {
        // Something else happened
        setUiMessage({
          type: "error",
          text: `Error: ${error.message}`,
        });
      }
    } finally {
      setIsUploading(false);
    }
  };

  // Enhanced backend error renderer
  const renderBackendErrors = () => {
    if (!backendErrors || !backendErrors.details) return null;

    return (
      <div
        style={{
          marginTop: "20px",
          padding: "15px",
          backgroundColor: "#fee",
          border: "1px solid #fcc",
          borderRadius: "5px",
        }}
      >
        <h4 style={{ color: "#d00", marginBottom: "10px" }}>
          Backend Validation Details
        </h4>
        <p>
          <strong>Step:</strong> {backendErrors.step}
        </p>
        <p>
          <strong>Error:</strong> {backendErrors.error}
        </p>
        {backendErrors.details && Array.isArray(backendErrors.details) && (
          <div style={{ marginTop: "10px" }}>
            <strong>Detailed Errors:</strong>
            <div
              style={{
                maxHeight: "200px",
                overflowY: "auto",
                marginTop: "5px",
              }}
            >
              {backendErrors.details.map((detail, index) => (
                <div
                  key={index}
                  style={{
                    padding: "5px",
                    backgroundColor: "#fff",
                    margin: "2px 0",
                    border: "1px solid #ddd",
                    borderRadius: "3px",
                  }}
                >
                  {detail.row && <strong>Row {detail.row}: </strong>}
                  {detail.errors
                    ? detail.errors.join(", ")
                    : detail.message || detail}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderValidationErrors = () => {
    if (
      !validationResult ||
      !Array.isArray(validationResult.validationErrors) ||
      validationResult.validationErrors.length === 0
    ) {
      return null;
    }

    return (
      <div
        style={{
          marginTop: "20px",
          padding: "15px",
          backgroundColor: "#fee",
          border: "1px solid #fcc",
          borderRadius: "5px",
        }}
      >
        <h4 style={{ color: "#d00", marginBottom: "10px" }}>
          Frontend Validation Errors ({validationResult.validationErrors.length}{" "}
          rows with errors)
        </h4>
        <p style={{ marginBottom: "15px", color: "#666" }}>
          Please fix these errors before proceeding:
        </p>

        <div style={{ maxHeight: "300px", overflowY: "auto" }}>
          {validationResult.validationErrors.map((error, index) => (
            <div
              key={index}
              style={{
                marginBottom: "10px",
                padding: "10px",
                backgroundColor: "#fff",
                border: "1px solid #ddd",
                borderRadius: "3px",
              }}
            >
              <strong style={{ color: "#d00" }}>
                Row {error.row} (Student ID: {error.studentId || "N/A"}):
              </strong>
              <ul style={{ margin: "5px 0 0 20px" }}>
                {error.errors.map((err, errIndex) => (
                  <li key={errIndex} style={{ color: "#666" }}>
                    {err}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Fragment>
      <Card>
        <CardHeader
          title="Download the template, fill it out, and upload it to update class/camp
            payments."
        >
          <CardHeaderToolbar>
            <a
              href="https://docs.google.com/spreadsheets/d/1BPTmN9QN0lAI0ULtMdeLRrXaN3lmFVxa/edit?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              <i className="fa fa-download"></i>
              Download Template
            </a>
          </CardHeaderToolbar>
        </CardHeader>
        <CardBody>
          {/* Important instruction message */}
          <div
            className="mb-5"
            style={{
              padding: "15px",
              backgroundColor: "#e3f2fd",
              border: "1px solid #90caf9",
              borderRadius: "5px",
            }}
          >
            <h5 style={{ color: "#1976d2", marginBottom: "10px" }}>
              <i className="fa fa-info-circle mr-2"></i>
              Important Instructions
            </h5>
            <p style={{ color: "#1976d2", marginBottom: "0" }}>
              When editing the template, make sure to use only the dropdown
              values provided for <strong>Course Code</strong>,{" "}
              <strong>Payment Method</strong>, and <strong>Branch</strong>{" "}
              fields. Using values outside the dropdown options will cause
              validation errors.
            </p>
          </div>

          <div className="mb-8">
            <h4 className="mb-3">Step 1: Select File</h4>
            <input
              type="file"
              accept=".xlsx"
              onChange={handleFileChange}
              ref={fileInputRef}
              className="form-control-file mb-2"
              style={{ marginRight: "10px" }}
            />
            {selectedFile && (
              <span className="text-muted">Selected: {selectedFile.name}</span>
            )}
          </div>

          <div className="mb-8">
            <h4 className="mb-3">Step 2: Validate Data</h4>
            <button
              onClick={handleValidateClick}
              className={`btn ${
                !selectedFile || isValidating ? "btn-secondary" : "btn-warning"
              }`}
              disabled={!selectedFile || isValidating}
              title={!selectedFile ? "Please select a file first" : ""}
            >
              {isValidating ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm mr-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Validating...
                </>
              ) : (
                <>
                  <i className="fa fa-check-circle mr-2"></i>
                  Validate File
                </>
              )}
            </button>
          </div>

          <div className="mb-8">
            <h4 className="mb-3">Step 3: Upload Data</h4>
            <button
              onClick={handleUploadClick}
              className={`btn ${
                !validationResult || !validationResult.isValid || isUploading
                  ? "btn-secondary"
                  : "btn-success"
              }`}
              disabled={
                !validationResult || !validationResult.isValid || isUploading
              }
            >
              {isUploading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm mr-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Uploading...
                </>
              ) : (
                <>
                  <i className="fa fa-upload mr-2"></i>
                  Upload Data
                </>
              )}
            </button>
          </div>

          {/* Message Banner for all types of messages */}
          {uiMessage && (
            <MessageBanner
              type={uiMessage.type}
              message={uiMessage.text}
              onClose={() => setUiMessage(null)}
            />
          )}

          {/* Render backend errors if any */}
          {renderBackendErrors()}

          {/* Render frontend validation errors */}
          {renderValidationErrors()}
        </CardBody>
      </Card>
    </Fragment>
  );
};

export default BulkPayUpload;
